import { Grid } from "@mui/material";
import {
  FormContainer,
  FormTabs,
  ITicketAutomation,
  Treturn,
  useFetch,
  useNotificationContext,
} from "@4uhub/lib4uhub";
import { useCallback, useMemo, useState } from "react";

import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import AccessTimeFilledIcon from "@mui/icons-material/AccessTimeFilled";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CancelOutlinedIcon from "@mui/icons-material/CancelOutlined";
import EditNoteIcon from "@mui/icons-material/EditNote";
import HealthAndSafetyIcon from "@mui/icons-material/HealthAndSafety";
import MedicalServices from "@mui/icons-material/MedicalServices";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ArticleIcon from "@mui/icons-material/Article";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import SpeakerNotesIcon from "@mui/icons-material/SpeakerNotes";
import LibraryBooksRoundedIcon from "@mui/icons-material/LibraryBooksRounded";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import ListAltIcon from "@mui/icons-material/ListAlt";
import TuneIcon from "@mui/icons-material/Tune";
import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import {
  automationReceivedRuleSchema,
  TAutomationReceivedRuleForm,
} from "./automationReceivedRuleSchema";
import {
  createAutomationReceivedRule,
  getAutomationReceivedRuleById,
  updateAutomationReceivedRuleById,
} from "../../../../../services/ticketAutomationReceivedRule.service";
import { BasicData } from "./BasicData/BasicData";
import AutomationReceivedSlotPage from "./AutomationReceivedSlot/AutomationReceivedSlotPage";
import InsuranceCategoryPage from "./InsuranceCategory/InsuranceCategoryPage";
import AutomationReceivedCustomMessagePage from "./AutomationReceivedCustomMessage/AutomationReceivedCustomMessagePage";
import PrivateInsurancePage from "./PrivateInsurance/PrivateInsurancePage";
import AutomationReceivedConfFilePage from "./AutomationReceivedConfFiles/AutomationReceivedConfFilesPage";
import AutomationReceivedDaysAheadPage from "./AutomationReceivedDaysAhead/AutomationReceivedDaysAheadPage";
import { AutomationReceivedMessagesPage } from "./AutomationReceivedMessages/AutomationReceivedMessagesPage";
import TicketAutRecRuleTasyOrientationPage from "./TicketAutRecRuleTasyOrientation/TicketAutRecRuleTasyOrientationPage";
import OfferClassificationPage from "./OfferClassification/OfferClassificationPage";
import PriceInsurancePage from "./PriceInsurance/PriceInsurancePage";
import CancelationReasonsPage from "./CancelationReasons/CancelationReasonsPage";
import EstablishmentsPage from "./Establishment/EstablishmentsPage";
import AllowedActionPage from "./AllowedAction/AllowedActionPage";
import AutomationRecRuleTicketDefDocPage from "./AutomationRecRuleTicketDefDoc/AutomationRecRuleTicketDefDocPage";
import AppointmentArrivalPage from "./AppointmentArrival/AppointmentArrivalPage";
import PreferencesPage from "./Preferences/PreferencesPage";
import UnchosenProfessionalPriorityPage from "./UnchosenProfessionalPriority/UnchosenProfessionalPriorityPage";

const translationPath = "chatBot.page.automations.automationReceivedRules.";

const AutomationReceivedRule = () => {
  const navigate = useNavigate();

  const { t } = useTranslation();

  const { setMessage } = useNotificationContext();

  const { eI, id } = useParams();

  const [code, setCode] = useState<number>();

  const { sendRequest: save, loading: sLoading } = useFetch(
    createAutomationReceivedRule,
  );

  const [ticketAutomation, setTicketAutomation] =
    useState<ITicketAutomation | null>(null);

  const { sendRequest: update, loading: uLoading } = useFetch(
    updateAutomationReceivedRuleById,
  );

  const { sendRequest: get, loading: gLoading } = useFetch(
    getAutomationReceivedRuleById,
  );

  const saveHandler = (v: TAutomationReceivedRuleForm) => {
    return save({
      ...v,
      ticketAutomationId: v.ticketAutomation.id,
      ticketChannelStepWhenErrorId: v.ticketChannelStepWhenError
        ? v.ticketChannelStepWhenError.id
        : undefined,
      ticketChannelStepWhenFinishedId: v.ticketChannelStepWhenFinished
        ? v.ticketChannelStepWhenFinished.id
        : undefined,
      transferMessage: v.transferMessage || undefined,
      errorMessage: v.errorMessage || undefined,
      name: v.ticketAutomation.name,
      description: v.description ? v.description : undefined,
      enable: v.enable,
      initialDate: v.initialDate?.toISOString().substring(0, 10),
      endDate: v.endDate?.toISOString().substring(0, 10),
    });
  };
  const updateHandler = (v: TAutomationReceivedRuleForm, id: string) => {
    return update({
      item: {
        ...v,
        id: v.id ? v.id : undefined,
        ticketAutomationId: v.ticketAutomation.id,
        ticketChannelStepWhenErrorId: v.ticketChannelStepWhenError
          ? v.ticketChannelStepWhenError.id
          : undefined,
        ticketChannelStepWhenFinishedId: v.ticketChannelStepWhenFinished
          ? v.ticketChannelStepWhenFinished.id
          : undefined,
        transferMessage: v.transferMessage || undefined,
        errorMessage: v.errorMessage || undefined,
        name: v.name,
        description: v.description ? v.description : undefined,
        enable: v.enable,
        initialDate: v.initialDate?.toISOString().substring(0, 10),
        endDate: v.endDate?.toISOString().substring(0, 10),
      },
      id: id,
    });
  };

  const getHandler = useCallback(
    async (id: string): Promise<Treturn<TAutomationReceivedRuleForm>> => {
      const { data, success } = await get(id);
      if (data) {
        setCode(data.code);
        setTicketAutomation(data.ticketAutomation);
        const newData: TAutomationReceivedRuleForm = {
          ...data,
          id: data.id,
          name: data.name,
          description: data.description,
          enable: data.enable,
          initialDate: new Date(data.initialDate),
          endDate: new Date(data.endDate),
          ticketAutomation: data.ticketAutomation,
          ticketChannelWhenError: data.ticketChannelStepWhenError
            ? data.ticketChannelStepWhenError.ticketChannel
            : undefined,
          ticketChannelWhenFinished:
            data.ticketChannelStepWhenFinished?.ticketChannel,
        };
        return { data: newData, success: true };
      }
      return { data, success };
    },
    [get],
  );

  const redirectOnSuccess = useCallback(
    (data?: any) => {
      if (!id) {
        if (data) {
          setMessage({
            message: t(translationPath + "created_successfully"),
            type: "success",
          });
          navigate(
            `/e/${eI}/chatBot/createAutomations/receivedAutomationRules/${data.id}/edit`,
          );
        }
      } else {
        navigate(`/e/${eI}/chatBot/createAutomations/receivedAutomationRules/`);
      }
    },
    [navigate, setMessage, t, id, eI],
  );

  const loading = sLoading || uLoading || gLoading;

  const tabs = useMemo(() => {
    const baseTabs = [];

    if (ticketAutomation?.code === "33") {
      baseTabs.push(
        {
          label: t(translationPath + "allowed_actions"),
          icon: <TaskAltIcon fontSize="small" />,
          component: <AllowedActionPage />,
        },
        {
          label: t(translationPath + "appointment_arrival_consultation"),
          icon: <AccessTimeFilledIcon fontSize="small" />,
          component: <AppointmentArrivalPage type="consultation" />,
        },
        {
          label: t(translationPath + "appointment_arrival_exam"),
          icon: <AccessTimeIcon fontSize="small" />,
          component: <AppointmentArrivalPage type="exam" />,
        },
        {
          label: t(translationPath + "establishment"),
          icon: <EditNoteIcon fontSize="small" />,
          component: (
            <EstablishmentsPage automationCode={ticketAutomation?.code} />
          ),
        },
      );
    } else {
      baseTabs.push(
        {
          label: t(translationPath + "private_insurance"),
          icon: <HealthAndSafetyIcon fontSize="small" />,
          component: <PrivateInsurancePage />,
        },
        {
          label: t(translationPath + "insurance"),
          icon: <MedicalServices fontSize="small" />,
          component: <InsuranceCategoryPage />,
        },
        {
          label: t(translationPath + "slot"),
          icon: <AccessTimeFilledIcon fontSize="small" />,
          component: (
            <AutomationReceivedSlotPage
              automationCode={ticketAutomation?.code}
            />
          ),
        },
        {
          label: t(translationPath + "days_ahead"),
          icon: <CalendarMonthIcon fontSize="small" />,
          component: (
            <AutomationReceivedDaysAheadPage
              automationCode={ticketAutomation?.code}
            />
          ),
        },
        {
          label: t(translationPath + "ticket_default_doc"),
          icon: <ListAltIcon fontSize="small" />,
          component: (
            <AutomationRecRuleTicketDefDocPage
              automationCode={ticketAutomation?.code}
            />
          ),
        },
      );
      if (ticketAutomation?.code === "25" || ticketAutomation?.code === "26") {
        baseTabs.push({
          label: t(translationPath + "messages"),
          icon: <SpeakerNotesIcon fontSize="small" />,
          component: (
            <AutomationReceivedMessagesPage
              automationCode={ticketAutomation?.code}
            />
          ),
        });
      }
      if (ticketAutomation?.code === "21" || ticketAutomation?.code === "20") {
        baseTabs.push({
          label: t(translationPath + "custom_message"),
          icon: <EditNoteIcon fontSize="small" />,
          component: (
            <AutomationReceivedCustomMessagePage
              automationCode={ticketAutomation?.code}
            />
          ),
        });
        const priceInsurancePage = {
          label: t(translationPath + "price_insurance"),
          icon: <LocalOfferIcon fontSize="small" />,
          component: <PriceInsurancePage />,
        };
        baseTabs.splice(2, 0, priceInsurancePage);
        baseTabs.splice(3, 0, {
          label: t(translationPath + "cancelation_reasons"),
          icon: <CancelOutlinedIcon fontSize="small" />,
          component: (
            <CancelationReasonsPage automationCode={ticketAutomation?.code} />
          ),
        });
      }
      if (
        ticketAutomation?.code === "20" ||
        ticketAutomation?.code === "21" ||
        ticketAutomation?.code === "26"
      ) {
        baseTabs.push({
          label: t(translationPath + "establishment"),
          icon: <EditNoteIcon fontSize="small" />,
          component: (
            <EstablishmentsPage automationCode={ticketAutomation?.code} />
          ),
        });
      }
      if (
        ticketAutomation?.code === "20" ||
        ticketAutomation?.code === "21" ||
        ticketAutomation?.code === "25" ||
        ticketAutomation?.code === "26"
      ) {
        baseTabs.push({
          label: t(translationPath + "tasy_orientation"),
          icon: <LibraryBooksRoundedIcon fontSize="small" />,
          component: (
            <TicketAutRecRuleTasyOrientationPage
              automationCode={ticketAutomation?.code}
            />
          ),
        });
      }
      if (ticketAutomation?.code === "21") {
        baseTabs.push({
          label: t(translationPath + "file"),
          icon: <ArticleIcon fontSize="small" />,
          component: (
            <AutomationReceivedConfFilePage
              automationCode={ticketAutomation?.code}
            />
          ),
        });
      }
      if (ticketAutomation?.code !== "21") {
        baseTabs.unshift({
          label: t(translationPath + "offer_classification"),
          icon: <StarBorderIcon fontSize="small" />,
          component: <OfferClassificationPage />,
        });
      }
      if (ticketAutomation?.code === "20") {
        baseTabs.push(
          {
            label: t(translationPath + "preferences"),
            icon: <TuneIcon fontSize="small" />,
            component: <PreferencesPage />,
          },
          {
            label: t(translationPath + "unchosenProfessionalPriorities"),
            icon: <SupervisorAccountIcon fontSize="small" />,
            component: <UnchosenProfessionalPriorityPage />,
          },
        );
      }
    }

    return baseTabs;
  }, [ticketAutomation, t]);

  return (
    <>
      <FormContainer<TAutomationReceivedRuleForm>
        saveFunction={saveHandler}
        updateFunction={updateHandler}
        getFunction={getHandler}
        loading={loading}
        title={t(translationPath + "automation_received_rule")}
        schema={automationReceivedRuleSchema}
        subtitle={t(translationPath + "new_automation_received_rule")}
        subtitleWatchField={"name"}
        backUrl={`/e/${eI}/chatBot/createAutomations/receivedAutomationRules/`}
        backButtonOnClick={() => navigate(-1)}
        redirectOnSave={false}
        onSuccess={redirectOnSuccess}
        formActionsOnBottom={false}
        marginBottom={5}
        customHeight={"fit-content"}
        fields={(type) => (
          <Grid container spacing={2}>
            <BasicData
              ticketAutomation={ticketAutomation}
              type={type}
              code={code}
            />
          </Grid>
        )}
      />
      {id && (
        <Grid container spacing={2} sx={{ width: "80%", margin: "0 auto" }}>
          <Grid item sm={12} mb={5}>
            <FormTabs tabs={tabs} />
          </Grid>
        </Grid>
      )}
    </>
  );
};

export default AutomationReceivedRule;
